import { useState } from 'react';
import { api } from '../api';
import type { Project } from '../api';
import { useSession } from '../session';
import { useData } from '../useData';
import { TagSelect } from './TagSelect';

/** Logging time is the thing the studio does most often, so it is a row of
 *  five fields wherever it appears rather than a page of its own.
 *
 *  Hours are typed as hours, because that is how people think: 1.5, or 1,5.
 *  The API stores minutes. */
export function LogTime({ onLogged }: { onLogged?: () => void }) {
  const { t } = useSession();
  const projects = useData(() => api.projects({ status: 'in_progress' }), []);

  const today = new Date().toISOString().slice(0, 10);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [workedOn, setWorkedOn] = useState(today);
  const [hours, setHours] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (projectId === '') return;
    const minutes = Math.round(parseFloat(hours.replace(',', '.')) * 60);
    if (!Number.isFinite(minutes) || minutes <= 0) return;

    setBusy(true);
    try {
      await api.logTime({ projectId, workedOn, minutes, title: title || undefined });
      setHours('');
      setTitle('');
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      onLogged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card form-grid">
      <div>
        <label htmlFor="lt-project">{t.project}</label>
        <TagSelect
          id="lt-project"
          value={projectId === '' ? undefined : projectId}
          placeholder={t.search}
          options={(projects.data ?? []).map((p: Project) => ({
            value: p.id,
            label: p.title,
            hint: p.workspaceName,
          }))}
          onChange={(v) => setProjectId(v ?? '')}
        />
      </div>
      <div>
        <label htmlFor="lt-when">{t.date}</label>
        <input id="lt-when" type="date" value={workedOn} onChange={(e) => setWorkedOn(e.target.value)} />
      </div>
      <div>
        <label htmlFor="lt-hours">{t.hours}</label>
        <input
          id="lt-hours"
          inputMode="decimal"
          placeholder="1.5"
          required
          value={hours}
          onChange={(e) => setHours(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="lt-what">{t.what}</label>
        <input id="lt-what" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="row">
        <button className="primary" type="submit" disabled={busy || projectId === '' || !hours}>
          {t.logTime} &gt;
        </button>
        {saved && <span className="muted">{t.saved}</span>}
      </div>
    </form>
  );
}
